<script setup lang="ts">
export type Props = {
  color?: 'black' | 'white' | 'transparent' | 'whiteFull' | 'red' | true | ''
  txt: stringI18n | undefined
  on?: boolean
  submit(): NotVoid
  icon?: boolean | 'right'
  grow?: 'never'
  height?: '34' | '40' | '50' | '80'
  big?: boolean
  width?: 'grow' | 'auto' | '40' | 'min100' | '200' | '100p' | 'min50' | 'min60' | 'min150'
  small?: boolean
  disabled?: boolean
}

const props = defineProps<Props>()
const data = shallowReactive({ on: false })
const inited = useInited('init2')
const mobile = useMobile()
const animate = useTemplateRef('animate')
const notify = useNotify()
const disabled2 = computed(() => inited.value && props.disabled)

function click(e: MouseEvent) {
  if (disabled2.value) return
  if (notify.notify.value.isLoading) return
  notify.watchFunc(() => props.submit())
  animate.value?.wave?.(e)
}
function enter(on: boolean, e: MouseEvent | FocusEvent | TouchEvent) {
  if (disabled2.value && on) return
  animate.value?.enter?.(on, e)
  data.on = on
}

const events = computed(() => {
  if (!inited.value) return {}
  const events: Record<string, unknown> = {
    onClickPassive: (e: MouseEvent) => click(e),
    onFocusPassive: (e: FocusEvent) => enter(true, e),
    onBlurPassive: (e: FocusEvent) => enter(false, e),
  }
  if (mobile.value) {
    events.onTouchstartPassive = (e: TouchEvent) => enter(true, e)
    events.onTouchendPassive = (e: TouchEvent) => enter(false, e)
  } else {
    events.onMouseenterPassive = (e: MouseEvent) => enter(true, e)
    events.onMouseleavePassive = (e: MouseEvent) => enter(false, e)
  }

  return events
})

const css = useCssModule()
const classes = computed((): string => {
  const { big, small, width, height, icon, color, txt } = props
  let res: string = ''
  const add = (...items: string[]): void => {
    for (const i of items) {
      res += i + ' '
    }
  }

  add(
    color === true || color === '' ? css.black : color ? css[color] : css.white,
    big ? '_fs09' : '_fs09',
    `_h${height || (big ? '50' : small ? '34' : '40')}`,
    `_w${width || (big ? 'min150' : small ? 'min60' : 'auto')}`
  )

  if (icon === 'right') add(css.icon2)
  else if (icon) add(css.icon)

  if (big) add('_pl14', '_pr14')
  else if (icon && txt) add(icon === 'right' ? '_pr04 _pl14' : '_pr14 _pl04')
  else add('_pl1', '_pr1')

  return res
})

defineExpose({ out: { submit: () => notify.elem.value?.click() } })

const uiOn = computed((): boolean => {
  if (data.on) return true
  const n = notify.notify.value
  return !!(n.error || n.isLoading || n.success)
})
</script>

<template>
  <button :ref="notify.elem" :class="[$style.btn, classes, on && 'on', uiOn && 'lod']" type="button" :disabled="disabled2" v-bind="events">
    <LazyUiNotify v-if="inited" v-memo="[notify]" :notify />
    <LazyButtonActionAnimation v-if="inited" ref="animate" :on="uiOn" :notify @update:on="(i) => (data.on = i)" />
    <slot v-if="icon !== 'right'" />{{ txt }}<slot v-if="icon === 'right'" />
  </button>
</template>

<style module>
.btn {
  composes: global(_fw-xl);
  composes: global(_ls-xs);
  composes: global(_gap1);
  composes: global(_relative);
  composes: global(_br99);
  composes: global(_hidden);
  composes: global(_hidden);
  composes: global(_noflex);
  composes: global(_ws_nowrap);

  z-index: 0;
  composes: global(_ctr);

  outline: none;
  --back2: var(--bg);
  --border0: var(--border, var(--back));
  --color0: var(--color);
  --delay: 200ms;
  border: 1px solid var(--border0, var(--black-5));
  color: var(--color0, var(--black));
  background: var(--back, var(--bg));
  transition:
    color 0ms linear var(--delay),
    border-color 0ms linear var(--delay);
  user-select: none;
}

.btn:not(:disabled):global(.lod),
:global(.d) .btn:not(:disabled):hover {
  --border0: var(--border2, var(--back2));
  --color0: var(--color2, var(--color));
  --svg-color: var(--svg2);
  --delay: 125ms;
  box-shadow: var(--shadow2);
}
.btn svg {
  color: var(--svg-color, currentColor);
}
.btn:disabled {
  --back: var(--black-6);
  --color: var(--black-4);
  --border: var(--black-5);
  cursor: not-allowed;
  opacity: 0.9;
}
.icon,
.icon2 {
  --svg-color: var(--black-45);
}
.black {
  --back: var(--black);
  --border: var(--black);
  --color: var(--bg);
  --svg-color: currentColor;
  --back2: var(--blue3);
  --color2: var(--bg);
}
.white {
  --back: var(--bg);
  --border: var(--black-5);
  --color: var(--black);
  --svg-color: currentColor;
  --back2: var(--blue3);
  --color2: var(--bg);
}
.transparent {
  --border: transparent;
  --color2: var(--black);
  --border2: var(--black-5);
  --delay: 0ms;
}
.transparent:focus {
  --border2: var(--black-5);
}
.transparent:global(.on) {
  --border2: var(--black-5);
}
.whiteFull {
  --back: var(--bg);
  --border: var(--black-5);
  --svg-color: var(--black);
  --border2: var(--black-5);
  --color2: var(--black);
  --back2: var(--bg);
  --svg2: var(--black);
  --shadow2: 1px 2px 0 hsla(240, 4%, 66%, 0.1);
}
.whiteFull:focus-visible {
  --border: var(--black);
}
.red {
  --back: red;
  --back2: var(--red);
  --border: red;
  --color: var(--bg);
  --color2: var(--bg);
  --svg-color: currentColor;
}
</style>
